// src/redux/rootReducer.ts
import authReducer from "@/src_new/shared/redux/reducers/authSlice";
import { AnyAction, combineReducers, Reducer, UnknownAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";

// Persist configs
import {
  authPersistConfig,
  masterCoveragePersistConfig,
  personalDetailsPersistConfig,
  quoteFlowPersistConfig,
} from "./persistConfig";

// Core slices

// Master slices
import masterSliceReducer from "@/src_new/shared/redux/reducers/master/masterSlice";
import masterCoverageReducer from "@/src_new/shared/redux/reducers/masterCoverageSlice";

// Feature slices
import lobListReducer from "@/src_new/features/leads/reduxer/listLobReduxer";
import commonSlice from "@/src_new/shared/redux/reducers/commonSlice";
import insurerSliceReducer from "@/src_new/shared/redux/reducers/insurer";
import leadSliceReducer from "@/src_new/shared/redux/reducers/leadSlice";
import insurerMasterSlice from "@/src_new/shared/redux/reducers/master/insurerMasterSlice";
import mobileCountryCodeReducer from "@/src_new/shared/redux/reducers/mobileCountryCodeSlice";
import personalDetailsReducer from "@/src_new/shared/redux/reducers/personalDetailsSlice";
import policySliceReducer from "@/src_new/shared/redux/reducers/policySlice";
import quoteFlowReducer from "@/src_new/shared/redux/reducers/quoteFlowSlice";
import quoteSliceReducer from "@/src_new/shared/redux/reducers/quoteSlice";
import rfqSlice from "@/src_new/shared/redux/reducers/rfq/index";
import { uiReducer } from "@/src_new/shared/redux/reducers/uiSlice";

//B2B
type RemoteSubmissionsModule = typeof import("logsApp/submissions");

// Try to load remote reducers. If remoteEntry is unavailable, keep host app running.
const remoteSubmissionsModule: Partial<RemoteSubmissionsModule> | null =
  await import("logsApp/submissions").catch((error) => {
    console.warn("[redux] Remote submissions module unavailable, using local fallbacks.", error);
    return null;
  });

const remoteSubmissionsDefault =
  (remoteSubmissionsModule?.default as Partial<RemoteSubmissionsModule> | undefined) ?? undefined;

const createNoopReducer = <S>(initialState: S): Reducer<S, UnknownAction> => {
  return (state = initialState) => state;
};

const ensureReducer = <S>(
  candidate: unknown,
  reducerName: string,
  initialState: S
): Reducer<S, UnknownAction> => {
  if (typeof candidate === "function") {
    return candidate as Reducer<S, UnknownAction>;
  }

  if (remoteSubmissionsModule) {
    console.warn(`[redux] Remote reducer '${reducerName}' missing, using fallback.`);
  }

  return createNoopReducer(initialState);
};

const resolvedSubmissionReducer = ensureReducer(
  remoteSubmissionsModule?.submissionReducer ?? remoteSubmissionsDefault?.submissionReducer,
  "submissionReducer",
  {}
);

const resolvedFormConfigReducer = ensureReducer(
  remoteSubmissionsModule?.formConfigReducer ??
    remoteSubmissionsModule?.selectLOBReducer ??
    remoteSubmissionsDefault?.formConfigReducer ??
    remoteSubmissionsDefault?.selectLOBReducer,
  "formConfigReducer",
  {}
);

const resolvedSelectLobReducer = ensureReducer(
  remoteSubmissionsModule?.selectLOBReducer ??
    remoteSubmissionsModule?.formConfigReducer ??
    remoteSubmissionsDefault?.selectLOBReducer ??
    remoteSubmissionsDefault?.formConfigReducer,
  "selectLOBReducer",
  {}
);

const resolvedRemoteUiReducer = ensureReducer(
  remoteSubmissionsModule?.remoteUiReducer ?? remoteSubmissionsDefault?.remoteUiReducer,
  "remoteUiReducer",
  {}
);

const appReducer = combineReducers({
  // Core
  ui: uiReducer,

  // Auth
  auth: persistReducer(authPersistConfig, authReducer),

  masterCoverage: persistReducer(masterCoveragePersistConfig, masterCoverageReducer),

  // Features
  quoteFlow: persistReducer(quoteFlowPersistConfig, quoteFlowReducer),
  sharedPersonalDetails: persistReducer(personalDetailsPersistConfig, personalDetailsReducer),

  // Common
  commonSlice: commonSlice,
  mobileCountryCode: mobileCountryCodeReducer,
  submission: resolvedSubmissionReducer,
  formConfig: resolvedFormConfigReducer,
  selectLOB: resolvedSelectLobReducer,
  remoteUi: resolvedRemoteUiReducer,
  lobList: lobListReducer,
  masterSlice: masterSliceReducer,
  leadSlice: leadSliceReducer,
  quoteSlice: quoteSliceReducer,
  policySlice: policySliceReducer,
  insurerSlice: insurerSliceReducer,
  insurerMasterSlice: insurerMasterSlice,
  rfqSlice: rfqSlice,
});

// export default rootReducer;

// export type RootState = ReturnType<typeof rootReducer>;
const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: AnyAction) => {
  return appReducer(state, action);
};

export default rootReducer;

export type RootState = ReturnType<typeof appReducer>;
